// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { create } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';
import type { UserRole, BaseContextPanelContent, AlarmContextSeed } from '../types';

export type ContextPanelContentType =
  | BaseContextPanelContent
  | { type: 'animal-detail'; animal: string };

interface ContextPanelState {
  isOpen: boolean;
  content: ContextPanelContentType | null;
}

/** Carbon theme keys used by the app shell (dark / light). */
export type CarbonTheme = 'g100' | 'g10';

/** Role-persona that drives the shell layout (mock's Operator/Manager/Executive). */
export type Persona = 'operator' | 'manager' | 'executive';

export interface A11yPrefs {
  highContrast: boolean;
  reduceMotion: boolean;
  largeText: boolean;
  soundAlerts: boolean;
}

export interface ShellState {
  viewMode: 'chat' | 'dashboard';
  selectedSiteId: string | null;
  selectedSiteName: string | null;
  contextPanel: ContextPanelState;
  mockRole: UserRole | null;
  chatSidebarOpen: boolean;
  sessionExpired: boolean;
  profileLoadError: boolean;

  // New Carbon shell state
  theme: CarbonTheme;
  a11y: A11yPrefs;
  /** Dev/manual persona override; when null, persona derives from the auth role. */
  personaOverride: Persona | null;
  subnavOpen: boolean;
  askHeliosOpen: boolean;
  /**
   * On-screen alarm the Ask Helios panel is scoped to. Threaded
   * into the first ask-helios request as ``context_seed`` so the orchestrator
   * resolves the bound SOP directly. Null when opened without an alarm.
   */
  askHeliosSeed: AlarmContextSeed | null;

  setViewMode: (_mode: 'chat' | 'dashboard') => void;
  toggleViewMode: () => void;
  setSelectedSite: (_id: string | null, _name?: string | null) => void;
  openContextPanel: (_content: ContextPanelContentType) => void;
  closeContextPanel: () => void;
  setMockRole: (_role: UserRole | null) => void;
  setChatSidebarOpen: (_open: boolean) => void;
  toggleChatSidebar: () => void;
  setSessionExpired: (_open: boolean) => void;
  setProfileLoadError: (_hasError: boolean) => void;

  setTheme: (_theme: CarbonTheme) => void;
  toggleTheme: () => void;
  setA11y: (_patch: Partial<A11yPrefs>) => void;
  setPersonaOverride: (_persona: Persona | null) => void;
  setSubnavOpen: (_open: boolean) => void;
  toggleSubnav: () => void;
  openAskHelios: (_seed?: AlarmContextSeed | null) => void;
  closeAskHelios: () => void;
  toggleAskHelios: () => void;
  /** Drops the alarm context without closing the panel (used by "New chat"). */
  clearAskHeliosSeed: () => void;
}

type ShellPersist = Pick<
  ShellState,
  | 'viewMode'
  | 'selectedSiteId'
  | 'selectedSiteName'
  | 'mockRole'
  | 'theme'
  | 'a11y'
  | 'personaOverride'
>;

const persistOptions: PersistOptions<ShellState, ShellPersist> = {
  name: 'helios_shell_state',
  partialize: state => ({
    viewMode: state.viewMode,
    selectedSiteId: state.selectedSiteId,
    selectedSiteName: state.selectedSiteName,
    mockRole: state.mockRole,
    theme: state.theme,
    a11y: state.a11y,
    personaOverride: state.personaOverride,
  }),
};

const DEFAULT_A11Y: A11yPrefs = {
  highContrast: true,
  reduceMotion: false,
  largeText: false,
  soundAlerts: false,
};

export const useShellStore = create<ShellState>()(
  persist(
    set => ({
      viewMode: 'chat',
      selectedSiteId: null,
      selectedSiteName: 'Fleet View',
      contextPanel: { isOpen: false, content: null },
      mockRole: null,
      chatSidebarOpen: false,
      sessionExpired: false,
      profileLoadError: false,

      theme: 'g100',
      a11y: DEFAULT_A11Y,
      personaOverride: null,
      subnavOpen: true,
      askHeliosOpen: false,
      askHeliosSeed: null,

      setViewMode: mode => set({ viewMode: mode }),
      toggleViewMode: () =>
        set(state => ({
          viewMode: state.viewMode === 'chat' ? 'dashboard' : 'chat',
        })),
      setSelectedSite: (id, name) => set({ selectedSiteId: id, selectedSiteName: name ?? null }),
      openContextPanel: content => set({ contextPanel: { isOpen: true, content } }),
      closeContextPanel: () => set({ contextPanel: { isOpen: false, content: null } }),
      setMockRole: role => set({ mockRole: role }),
      setChatSidebarOpen: open => set({ chatSidebarOpen: open }),
      toggleChatSidebar: () => set(state => ({ chatSidebarOpen: !state.chatSidebarOpen })),
      setSessionExpired: open => set({ sessionExpired: open }),
      setProfileLoadError: hasError => set({ profileLoadError: hasError }),

      setTheme: theme => set({ theme }),
      toggleTheme: () => set(state => ({ theme: state.theme === 'g100' ? 'g10' : 'g100' })),
      setA11y: patch => set(state => ({ a11y: { ...state.a11y, ...patch } })),
      setPersonaOverride: persona => set({ personaOverride: persona }),
      setSubnavOpen: open => set({ subnavOpen: open }),
      toggleSubnav: () => set(state => ({ subnavOpen: !state.subnavOpen })),
      openAskHelios: seed => set({ askHeliosOpen: true, askHeliosSeed: seed ?? null }),
      closeAskHelios: () => set({ askHeliosOpen: false, askHeliosSeed: null }),
      toggleAskHelios: () => set(state => ({ askHeliosOpen: !state.askHeliosOpen })),
      clearAskHeliosSeed: () => set({ askHeliosSeed: null }),
    }),
    persistOptions
  )
);
